import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
// @ts-ignore - react-signature-canvas types are incompatible with React 18
import SignatureCanvas from 'react-signature-canvas'
import {
  getFormPublic,
  getFormFileUrl,
  submitSignature,
  FormPublicData,
} from '../../services/api'

export default function SignForm() {
  const { t } = useTranslation()
  const { formId } = useParams<{ formId: string }>()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || undefined

  const [form, setForm] = useState<FormPublicData | null>(null)
  const [fileUrl, setFileUrl] = useState<string>('')
  const [fileType, setFileType] = useState<string>('')
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({})
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const [showSignModal, setShowSignModal] = useState(false)
  const [notActivated, setNotActivated] = useState(false)
  const [agreeToSign, setAgreeToSign] = useState(false)
  const signatureRef = useRef<any>(null)
  const signModalRef = useRef<HTMLDivElement>(null)
  const signTriggerRef = useRef<HTMLButtonElement>(null)

  // Modal focus trap + Escape
  const handleModalKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') { setShowSignModal(false); return }
    if (e.key === 'Tab' && signModalRef.current) {
      const focusable = signModalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus() }
    }
  }, [])

  useEffect(() => {
    if (showSignModal) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', handleModalKeyDown)
      requestAnimationFrame(() => {
        signModalRef.current?.querySelector<HTMLElement>('input, button')?.focus()
      })
    } else {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', handleModalKeyDown)
      signTriggerRef.current?.focus()
    }
    return () => { document.removeEventListener('keydown', handleModalKeyDown) }
  }, [showSignModal, handleModalKeyDown])

  useEffect(() => { document.title = `${form?.title || t('sign.fillAndSign')} | ${t('common.appName')}` }, [form, t])

  useEffect(() => {
    loadForm()
  }, [formId, token])

  const loadForm = async () => {
    if (!formId) return

    setLoading(true)
    setError('')

    try {
      const formData = await getFormPublic(formId, token)

      setForm(formData)
      setFileUrl(getFormFileUrl(formId))
      setFileType(formData.fileType)

      // Set default values from link
      const defaults: Record<string, string> = {}
      formData.fields.forEach((field) => {
        defaults[field.id] = formData.defaults[field.id] || ''
      })
      setFieldValues(defaults)
    } catch (err: any) {
      // Check if form is not activated (403)
      if (err.message?.includes('not been activated')) {
        setNotActivated(true)
      }
      setError(err instanceof Error ? err.message : t('errors.loadingError'))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!form || !formId) return

    // Validate required fields
    for (const field of form.fields) {
      if (field.required && !fieldValues[field.id]?.trim()) {
        setError(t('sign.validation.fieldRequired', { field: field.name }))
        return
      }
    }

    // Validate signature
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError(t('sign.validation.signatureRequired'))
      return
    }

    // Validate electronic signing consent
    if (!agreeToSign) {
      setError(t('sign.validation.consentRequired'))
      return
    }

    setSubmitting(true)
    setError('')

    try {
      const signatureImage = signatureRef.current.toDataURL('image/png')
      await submitSignature(formId, fieldValues, signatureImage, token)
      setSubmitted(true)
      setShowSignModal(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.submitError'))
    } finally {
      setSubmitting(false)
    }
  }

  const clearSignature = () => {
    signatureRef.current?.clear()
  }

  if (loading) {
    return (
      <div className="container text-center" style={{ paddingTop: '4rem' }} aria-busy="true">
        <FontAwesomeIcon icon="spinner" className="spinner" style={{ fontSize: '2rem' }} aria-hidden="true" />
        <p className="mt-4 text-gray" role="status">{t('common.loading')}</p>
      </div>
    )
  }

  if (notActivated) {
    return (
      <div className="container">
        <div className="card text-center" style={{ maxWidth: '500px', margin: '3rem auto' }}>
          <FontAwesomeIcon
            icon="lock"
            style={{ fontSize: '3rem', color: 'var(--gray-500)', marginBottom: '1rem' }}
          />
          <h1>{t('sign.notActivated.title')}</h1>
          <p className="text-gray">{t('sign.notActivated.message')}</p>
        </div>
      </div>
    )
  }

  if (error && !form) {
    return (
      <div className="container">
        <div className="card text-center" style={{ maxWidth: '500px', margin: '3rem auto' }}>
          <FontAwesomeIcon
            icon="xmark"
            style={{ fontSize: '3rem', color: 'var(--danger)', marginBottom: '1rem' }}
          />
          <h1>{t('common.error')}</h1>
          <p className="text-gray">{error}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="container">
        <div className="card text-center" style={{ maxWidth: '500px', margin: '3rem auto' }} role="status">
          <FontAwesomeIcon
            icon="check"
            style={{ fontSize: '3rem', color: 'var(--success)', marginBottom: '1rem' }}
          />
          <h1>{t('sign.success.title')}</h1>
          <p className="text-gray">{t('sign.success.message')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div className="text-center mb-4">
          <h1>{form?.title}</h1>
          {form?.linkLabel && (
            <span className="badge badge-success">{form.linkLabel}</span>
          )}
        </div>

        {/* Form Preview */}
        <div className="card mb-4">
          <h3>{t('sign.documentPreview')}</h3>
          <div className="form-preview">
            {fileType === 'pdf' ? (
              <iframe src={fileUrl} title={t('sign.documentPreview')} />
            ) : (
              <img src={fileUrl} alt={t('sign.documentPreview')} />
            )}
          </div>
        </div>

        {/* Sign Button */}
        <button
          ref={signTriggerRef}
          className="btn btn-primary btn-lg w-full"
          onClick={() => setShowSignModal(true)}
        >
          <FontAwesomeIcon icon="signature" />
          {t('sign.fillAndSign')}
        </button>

        {/* Sign Modal */}
        {showSignModal && (
          <div className="modal-overlay" onClick={() => setShowSignModal(false)}>
            <div
              ref={signModalRef}
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sign-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 id="sign-modal-title">{t('sign.modalTitle')}</h3>
                <button className="btn btn-outline btn-icon" onClick={() => setShowSignModal(false)} aria-label={t('common.close')}>
                  <FontAwesomeIcon icon="xmark" />
                </button>
              </div>
              <div className="modal-body">
                {/* Fields */}
                {form?.fields.map((field) => (
                  <div key={field.id} className="form-group">
                    <label className="form-label" htmlFor={`sign-field-${field.id}`}>
                      {field.name}
                      {field.required && <span className="text-danger"> *</span>}
                    </label>
                    <input
                      id={`sign-field-${field.id}`}
                      type={field.type}
                      className="form-input"
                      value={fieldValues[field.id] || ''}
                      onChange={(e) =>
                        setFieldValues({ ...fieldValues, [field.id]: e.target.value })
                      }
                      placeholder={t('sign.fieldPlaceholder', { field: field.name })}
                      dir={field.type === 'email' || field.type === 'tel' ? 'ltr' : undefined}
                    />
                  </div>
                ))}

                {/* Signature Canvas */}
                <div className="form-group">
                  <div className="flex items-center justify-between mb-2">
                    <label className="form-label" style={{ marginBottom: 0 }}>
                      {t('sign.signatureLabel')} <span className="text-danger">*</span>
                    </label>
                    <button className="btn btn-outline" onClick={clearSignature}>
                      <FontAwesomeIcon icon="eraser" />
                      {t('sign.clear')}
                    </button>
                  </div>
                  <div className="signature-canvas-container" role="img" aria-label={t('sign.signatureLabel')} tabIndex={0}>
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        width: 500,
                        height: 200,
                        style: { width: '100%', height: '200px' },
                      }}
                      backgroundColor="white"
                    />
                  </div>
                  <p className="text-sm text-gray mt-2">{t('sign.signatureHelp')}</p>
                </div>

                {/* Electronic signing consent */}
                <div className="sign-consent">
                  <label className="sign-consent-label">
                    <input
                      type="checkbox"
                      checked={agreeToSign}
                      onChange={(e) => setAgreeToSign(e.target.checked)}
                    />
                    <span>{t('sign.consentText')}</span>
                  </label>
                </div>

                {error && <p className="text-danger text-sm" role="alert">{error}</p>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline" onClick={() => setShowSignModal(false)}>
                  {t('common.cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={submitting || !agreeToSign}
                >
                  {submitting ? (
                    <>
                      <FontAwesomeIcon icon="spinner" className="spinner" />
                      {t('sign.submitting')}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon="check" />
                      {t('sign.submitSignature')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
